/**
 * Main-dashboard widgets backed by real data (store/workspaceDataStore).
 *
 * Every widget reads a live source. While loading it shows a skeleton; when
 * the source fails it says why (no access / service error) with a Retry — it
 * never falls back to invented numbers. Each widget links to the screen that
 * owns the data.
 */
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { AlertCircle, ArrowRight, Lock, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { cn } from '@/lib/utils';
import { CHART, CHART_AXIS, CHART_GRID, CHART_TOOLTIP, CHART_TOOLTIP_ITEM } from '@/lib/chartColors';
import { useAuthStore } from '@/store/authStore';
import {
  selectSource, useWorkspaceDataStore, type MoneyRow, type SourceKey, type SourceState,
} from '@/store/workspaceDataStore';

// ───────────────────────────── helpers ─────────────────────────────

function useSource<K extends SourceKey>(key: K) {
  const state = useWorkspaceDataStore(selectSource(key));
  const load = useWorkspaceDataStore((s) => s.load);
  useEffect(() => { load(key); }, [key, load]);
  return state;
}

const fmtNum = (n: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
const fmtAmount = (n: number) => new Intl.NumberFormat(undefined, { notation: n >= 100000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(n);
const fmtDay = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—');
const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d < 30 ? `${d} d ago` : fmtDay(iso);
};
const titleCase = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Frame shared by every widget: loading skeleton, error / no-access message with
 * Retry, then the content and a footer link to the owning screen.
 */
function Shell({ sources, to, linkLabel, children }: {
  sources: { key: SourceKey; state: SourceState }[];
  to?: string;
  linkLabel?: string;
  children: ReactNode;
}) {
  const load = useWorkspaceDataStore((s) => s.load);
  const failed = sources.find((s) => s.state.error);
  const loading = sources.some((s) => !s.state.data && !s.state.error);

  let body: ReactNode = children;
  if (failed) {
    const noAccess = failed.state.status === 403;
    body = (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-3 text-center">
        {noAccess ? <Lock className="size-5 text-muted-foreground" /> : <AlertCircle className="size-5 text-warning" />}
        <p className="text-sm font-medium text-foreground">{noAccess ? 'No access' : "Couldn't load this"}</p>
        <p className="line-clamp-3 text-xs text-muted-foreground" title={failed.state.detail || undefined}>{failed.state.error}</p>
        {!noAccess && (
          <Button variant="tertiary" size="xs" className="mt-1" onClick={() => sources.forEach((s) => load(s.key, true))}>
            <RefreshCw /> Retry
          </Button>
        )}
      </div>
    );
  } else if (loading) {
    body = (
      <div className="space-y-2 p-1" aria-busy="true">
        <div className="h-6 w-1/3 animate-pulse rounded bg-secondary" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-secondary" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-secondary" />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col p-4">
      <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
      {to && (
        <Link to={to} className="mt-2 inline-flex items-center gap-1 self-start text-xs font-medium text-primary hover:underline">
          {linkLabel || 'Open'} <ArrowRight className="size-3" />
        </Link>
      )}
    </div>
  );
}

function Metric({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div>
      <div className="font-display text-metric tabular-nums leading-none text-foreground">{value}</div>
      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function Stat({ value, label, tone }: { value: ReactNode; label: string; tone?: 'success' | 'warning' | 'destructive' | 'info' }) {
  return (
    <div className="min-w-0 rounded-md border border-border px-2 py-1.5" title={label}>
      <div className={cn('text-base font-semibold tabular-nums',
        tone === 'success' ? 'text-success-strong' : tone === 'warning' ? 'text-warning-strong'
          : tone === 'destructive' ? 'text-destructive' : tone === 'info' ? 'text-info' : 'text-foreground')}>{value}</div>
      <p className="truncate text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

const Count = ({ n, tone }: { n: number; tone?: 'warning' }) => (
  <span className={cn('tabular-nums text-sm font-medium', tone === 'warning' ? 'text-warning-strong' : 'text-foreground')}>{fmtNum(n)}</span>
);

function Empty({ text }: { text: string }) {
  return <p className="py-4 text-center text-sm text-muted-foreground">{text}</p>;
}

function Rows({ items }: { items: { key: string; primary: ReactNode; secondary?: ReactNode; right?: ReactNode }[] }) {
  return (
    <ul className="divide-y divide-border">
      {items.map((r) => (
        <li key={r.key} className="flex items-center justify-between gap-3 py-1.5">
          <div className="min-w-0">
            <p className="truncate text-sm text-foreground">{r.primary}</p>
            {r.secondary && <p className="truncate text-xs text-muted-foreground">{r.secondary}</p>}
          </div>
          {r.right && <div className="shrink-0 text-xs text-muted-foreground">{r.right}</div>}
        </li>
      ))}
    </ul>
  );
}

// ───────────────────────────── People ─────────────────────────────

export function PeopleOverviewWidget() {
  const pd = useSource('peopleDashboard');
  const d: any = pd.data;
  const org = d?.org;
  return (
    <Shell sources={[{ key: 'peopleDashboard', state: pd }]} to="/people" linkLabel="People hub">
      {org ? (
        <div className="space-y-3">
          <Metric value={fmtNum(org.headcount.active)} label="Active employees" />
          <Rows items={[
            { key: 'in', primary: 'Present today', right: <Count n={org.attendanceToday.present} /> },
            { key: 'new', primary: 'Joined in the last 30 days', right: <Count n={org.headcount.joined30d} /> },
            { key: 'onb', primary: 'Onboarding now', right: <Count n={org.lifecycle.onboarding} /> },
          ]} />
        </div>
      ) : d?.me ? (
        <div className="space-y-3">
          <Metric value={d.me.employee.name} label={[d.me.employee.designation, d.me.employee.department].filter(Boolean).join(' · ') || 'Your profile'} />
          <div className="grid grid-cols-2 gap-2">
            <Stat value={`${d.me.attendance.hours} h`} label="Worked today" />
            <Stat value={d.me.leaveBalances.reduce((n: number, b: any) => n + b.remaining, 0)} label="Leave days left" />
          </div>
        </div>
      ) : <Empty text="No people data." />}
    </Shell>
  );
}

/** Company attendance today; for people without the team view, their own day. */
export function AttendanceSummaryWidget() {
  const summary = useSource('attendanceSummary');
  const pd = useSource('peopleDashboard');
  const own = summary.status === 403;
  const s: any = summary.data;
  const me: any = (pd.data as any)?.me;
  return (
    <Shell sources={own ? [{ key: 'peopleDashboard', state: pd }] : [{ key: 'attendanceSummary', state: summary }]}
      to="/people/attendance" linkLabel="Attendance">
      {!own && s ? (
        <div className="space-y-3">
          <Metric value={<>{s.present}<span className="text-muted-foreground"> / {s.totalEmployees}</span></>} label="Present today" />
          <Rows items={[
            { key: 'now', primary: 'Checked in now', right: <Count n={s.checkedInNow} /> },
            { key: 'late', primary: 'Late', right: <Count n={s.late} tone={s.late ? 'warning' : undefined} /> },
            { key: 'not', primary: 'Not checked in', right: <Count n={s.notCheckedIn} /> },
            { key: 'miss', primary: 'Missing check-out', right: <Count n={s.missingCheckout} tone={s.missingCheckout ? 'warning' : undefined} /> },
          ]} />
          <p className="text-xs text-muted-foreground">{s.onLeave} on leave · {s.wfh} working from home</p>
        </div>
      ) : me ? (
        <div className="space-y-3">
          <Metric value={`${me.attendance.hours} h`} label="You worked today" />
          <Badge variant={me.attendance.status === 'checked_in' ? 'success' : me.attendance.status === 'on_leave' ? 'info' : 'muted'}>
            {titleCase(me.attendance.status)}
          </Badge>
        </div>
      ) : <Empty text="No attendance data." />}
    </Shell>
  );
}

/** Attendance over the last two weeks (company), or your week. Replaces the old fake finance chart. */
export function AttendanceTrendWidget() {
  const pd = useSource('peopleDashboard');
  const d: any = pd.data;
  const org = d?.org;
  const data = org
    ? org.attendanceTrend.map((p: any) => ({ day: fmtDay(p.date), Office: Math.max(0, p.present - p.remote), 'Home / remote': p.remote }))
    : (d?.me?.week || []).map((p: any) => ({ day: fmtDay(p.date), Hours: p.hours }));
  return (
    <Shell sources={[{ key: 'peopleDashboard', state: pd }]} to="/people/attendance?tab=history" linkLabel="Attendance history">
      <div className="flex h-full min-h-[160px] flex-col">
        <p className="mb-2 text-xs text-muted-foreground">{org ? 'People present per day, last 14 days' : 'Your hours this week'}</p>
        <div className="min-h-0 flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barCategoryGap="30%">
              <CartesianGrid vertical={false} stroke={CHART_GRID} />
              <XAxis dataKey="day" tick={{ fill: CHART_AXIS, fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fill: CHART_AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={CHART_TOOLTIP} itemStyle={CHART_TOOLTIP_ITEM} cursor={{ fill: 'hsl(var(--secondary))' }} />
              {org ? (
                <>
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Office" stackId="a" fill={CHART.blue} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Home / remote" stackId="a" fill={CHART.sky} radius={[4, 4, 0, 0]} />
                </>
              ) : <Bar dataKey="Hours" fill={CHART.blue} radius={[4, 4, 0, 0]} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Shell>
  );
}

export function LeaveWidget() {
  const pd = useSource('peopleDashboard');
  const d: any = pd.data;
  const balances = (d?.me?.leaveBalances || []).filter((b: any) => b.allocated > 0).slice(0, 3);
  const pending = d?.org?.pendingLeave;
  return (
    <Shell sources={[{ key: 'peopleDashboard', state: pd }]} to="/people/leave" linkLabel="Leave">
      <div className="space-y-3">
        {pending && <Metric value={pending.total} label="Leave requests waiting for approval" />}
        {balances.length ? (
          <Rows items={balances.map((b: any) => ({ key: b.code, primary: b.name, right: <span className="tabular-nums text-foreground">{b.remaining} / {b.allocated} days</span> }))} />
        ) : !pending && <Empty text="No leave allocated to you yet." />}
      </div>
    </Shell>
  );
}

export function PayrollWidget() {
  const stats = useSource('payrollStats');
  const pd = useSource('peopleDashboard');
  const own = stats.status === 403;
  const s: any = stats.data;
  const slip: any = (pd.data as any)?.me?.latestPayslip;
  const month = new Date().toLocaleDateString(undefined, { month: 'long' });
  const slipLine = slip && (
    <p className="text-xs text-muted-foreground">
      Your latest payslip: {slip.currency} {fmtAmount(slip.net)} net · {fmtDay(slip.periodStart)} – {fmtDay(slip.periodEnd)} · {titleCase(slip.status)}
    </p>
  );
  return (
    <Shell sources={own ? [{ key: 'peopleDashboard', state: pd }] : [{ key: 'payrollStats', state: stats }]} to="/people/payroll" linkLabel="Payroll">
      {!own && s ? (
        <div className="space-y-3">
          {s.total.count === 0 ? (
            <Metric value="—" label={`No payroll run for ${month} yet`} />
          ) : (
            <>
              <Metric value={fmtAmount(s.total.amount)} label={`${month} payroll · ${s.total.count} payslips`} />
              <Rows items={[
                { key: 'd', primary: 'Draft', right: <Count n={s.draft.count} /> },
                { key: 'p', primary: 'Processed', right: <Count n={s.processed.count} /> },
                { key: 'pd', primary: 'Paid', right: <Count n={s.paid.count} /> },
              ]} />
            </>
          )}
          {slipLine}
        </div>
      ) : slip ? (
        <div className="space-y-2">
          <Metric value={`${slip.currency} ${fmtAmount(slip.net)}`} label={`Your net pay · ${fmtDay(slip.periodStart)} – ${fmtDay(slip.periodEnd)}`} />
          <Badge variant={slip.status === 'paid' ? 'success' : 'info'}>{titleCase(slip.status)}</Badge>
        </div>
      ) : <Empty text="No payslips yet." />}
    </Shell>
  );
}

// ───────────────────────────── Assets ─────────────────────────────

export function AssetsOverviewWidget() {
  const st = useSource('assets');
  const rows = st.data || [];
  const by = (s: string) => rows.filter((a) => a.status.toLowerCase() === s).length;
  const assigned = by('assigned');
  const available = rows.filter((a) => ['available', 'in stock', 'in_stock'].includes(a.status.toLowerCase())).length;
  return (
    <Shell sources={[{ key: 'assets', state: st }]} to="/assets" linkLabel="Assets hub">
      {rows.length === 0 ? <Empty text="No assets registered yet." /> : (
        <div className="space-y-3">
          <Metric value={fmtNum(rows.length)} label="Assets registered" />
          <Rows items={[
            { key: 'as', primary: 'Assigned to people', right: <Count n={assigned} /> },
            { key: 'av', primary: 'Available', right: <Count n={available} /> },
            { key: 'ot', primary: 'In repair, retired or other', right: <Count n={rows.length - assigned - available} /> },
          ]} />
        </div>
      )}
    </Shell>
  );
}

export function AssetsRegistryWidget() {
  const st = useSource('assets');
  const rows = st.data || [];
  const cats = Object.entries(rows.reduce<Record<string, { total: number; assigned: number }>>((m, a) => {
    const k = a.category || 'Uncategorised';
    m[k] = m[k] || { total: 0, assigned: 0 };
    m[k].total += 1;
    if (a.assignedTo) m[k].assigned += 1;
    return m;
  }, {})).sort((a, b) => b[1].total - a[1].total).slice(0, 5);
  return (
    <Shell sources={[{ key: 'assets', state: st }]} to="/assets/registry" linkLabel="Asset registry">
      {rows.length === 0 ? <Empty text="No assets registered yet." /> : (
        <Rows items={cats.map(([name, c]) => ({ key: name, primary: name, secondary: `${c.assigned} assigned · ${c.total - c.assigned} unassigned`, right: <span className="tabular-nums text-foreground">{c.total}</span> }))} />
      )}
    </Shell>
  );
}

export function AssetsMaintenanceWidget() {
  const st = useSource('maintenance');
  const open = (st.data || []).filter((m) => !['completed', 'cancelled', 'done'].includes((m.status || '').toLowerCase()))
    .sort((a, b) => String(a.scheduledFor || '9').localeCompare(String(b.scheduledFor || '9'))).slice(0, 5);
  return (
    <Shell sources={[{ key: 'maintenance', state: st }]} to="/assets/service" linkLabel="Maintenance">
      {open.length === 0 ? <Empty text="No maintenance scheduled." /> : (
        <Rows items={open.map((m) => ({ key: m.id, primary: m.asset || 'Asset', secondary: m.type || m.description || '', right: fmtDay(m.scheduledFor) }))} />
      )}
    </Shell>
  );
}

// ───────────────────────────── Support ─────────────────────────────

export function SupportOverviewWidget() {
  const st = useSource('supportQueues');
  const queues = st.data || [];
  const total = queues.reduce((n, q) => n + q.count, 0);
  return (
    <Shell sources={[{ key: 'supportQueues', state: st }]} to="/support" linkLabel="Support hub">
      <div className="space-y-3">
        <Metric value={fmtNum(total)} label="Tickets in queues" />
        <Rows items={queues.map((q) => ({ key: q.key, primary: q.label, right: <Count n={q.count} /> }))} />
      </div>
    </Shell>
  );
}

export function SupportSlaWidget() {
  const st = useSource('supportTickets');
  const tickets = st.data || [];
  const breached = tickets.filter((t) => t.slaBreached);
  return (
    <Shell sources={[{ key: 'supportTickets', state: st }]} to="/support" linkLabel="Tickets">
      <div className="space-y-3">
        <Metric value={breached.length} label="Tickets past their SLA" />
        {breached.length > 0 && (
          <Rows items={breached.slice(0, 4).map((t) => ({ key: t.id, primary: t.subject, secondary: t.number || undefined, right: t.priority ? titleCase(t.priority) : undefined }))} />
        )}
      </div>
    </Shell>
  );
}

// ───────────────────────────── Work ─────────────────────────────

export function ProjectsWidget() {
  const st = useSource('projects');
  const rows = st.data || [];
  const active = rows.filter((p) => ['active', 'in_progress', 'in progress'].includes(p.status.toLowerCase()));
  return (
    <Shell sources={[{ key: 'projects', state: st }]} to="/work/projects" linkLabel="Projects">
      {rows.length === 0 ? <Empty text="No projects yet." /> : (
        <div className="space-y-3">
          <Metric value={active.length} label={`Active projects (of ${rows.length})`} />
          <Rows items={active.slice(0, 4).map((p) => ({ key: p.id, primary: p.name, right: p.endDate ? `Due ${fmtDay(p.endDate)}` : undefined }))} />
        </div>
      )}
    </Shell>
  );
}

export function TasksWidget() {
  const st = useSource('tasks');
  const me = useAuthStore((s) => s.user?.id);
  const openTasks = (st.data || []).filter((t) => !['done', 'completed', 'cancelled', 'closed'].includes(t.status.toLowerCase()));
  const mine = openTasks.filter((t) => t.assigneeId && t.assigneeId === me);
  const overdue = openTasks.filter((t) => t.dueDate && new Date(t.dueDate) < new Date());
  const show = (mine.length ? mine : openTasks).sort((a, b) => String(a.dueDate || '9').localeCompare(String(b.dueDate || '9'))).slice(0, 4);
  return (
    <Shell sources={[{ key: 'tasks', state: st }]} to="/work/tasks" linkLabel="Tasks">
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <Stat value={mine.length} label="Assigned to you" />
          <Stat value={openTasks.length} label="Open in total" />
          <Stat value={overdue.length} label="Overdue" tone={overdue.length ? 'destructive' : undefined} />
        </div>
        {show.length ? (
          <Rows items={show.map((t) => ({ key: t.id, primary: t.title, secondary: titleCase(t.status), right: t.dueDate ? fmtDay(t.dueDate) : undefined }))} />
        ) : <Empty text="No open tasks." />}
      </div>
    </Shell>
  );
}

export function TimesheetsWidget() {
  const st = useSource('timesheets');
  const rows = st.data || [];
  const waiting = rows.filter((t) => (t.status || '').toLowerCase() === 'submitted');
  return (
    <Shell sources={[{ key: 'timesheets', state: st }]} to="/work/timesheets" linkLabel="Timesheets">
      <div className="space-y-3">
        <Metric value={waiting.length} label="Timesheets waiting for approval" />
        <p className="text-xs text-muted-foreground">{rows.length ? `${rows.length} timesheets in total` : 'No timesheets submitted yet.'}</p>
      </div>
    </Shell>
  );
}

// ───────────────────────────── Finance ─────────────────────────────

const isOpenInvoice = (i: MoneyRow) => !['paid', 'cancelled', 'void', 'draft'].includes((i.status || '').toLowerCase());

export function FinanceOverviewWidget() {
  const st = useSource('invoices');
  const rows = st.data || [];
  const open = rows.filter(isOpenInvoice);
  const overdue = open.filter((i) => i.dueDate && new Date(i.dueDate) < new Date());
  return (
    <Shell sources={[{ key: 'invoices', state: st }]} to="/money" linkLabel="Finance hub">
      {rows.length === 0 ? <Empty text="No invoices yet." /> : (
        <div className="space-y-3">
          <Metric value={fmtAmount(open.reduce((n, i) => n + i.amount, 0))} label={`Outstanding on ${open.length} invoices`} />
          <div className="grid grid-cols-2 gap-2">
            <Stat value={overdue.length} label="Overdue" tone={overdue.length ? 'destructive' : undefined} />
            <Stat value={rows.length - open.length} label="Paid / closed" tone="success" />
          </div>
        </div>
      )}
    </Shell>
  );
}

export function FinanceChartWidget() {
  const st = useSource('invoices');
  const rows = st.data || [];
  const months = Array.from({ length: 6 }, (_, k) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - (5 - k));
    return { key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleDateString(undefined, { month: 'short' }), Invoiced: 0, Paid: 0 };
  });
  for (const i of rows) {
    if (!i.date) continue;
    const d = new Date(i.date);
    const m = months.find((x) => x.key === `${d.getFullYear()}-${d.getMonth()}`);
    if (!m) continue;
    m.Invoiced += i.amount;
    if ((i.status || '').toLowerCase() === 'paid') m.Paid += i.amount;
  }
  return (
    <Shell sources={[{ key: 'invoices', state: st }]} to="/money/invoices" linkLabel="Invoices">
      {rows.length === 0 ? <Empty text="No invoices in the last 6 months." /> : (
        <div className="h-full min-h-[160px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={months} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={CHART_GRID} />
              <XAxis dataKey="label" tick={{ fill: CHART_AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: CHART_AXIS, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={fmtAmount} />
              <Tooltip contentStyle={CHART_TOOLTIP} itemStyle={CHART_TOOLTIP_ITEM} formatter={(v: number) => fmtAmount(v)} cursor={{ fill: 'hsl(var(--secondary))' }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Invoiced" fill={CHART.blue} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Paid" fill={CHART.green} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Shell>
  );
}

export function InvoicesWidget() {
  const st = useSource('invoices');
  const open = (st.data || []).filter(isOpenInvoice).sort((a, b) => String(a.dueDate || '9').localeCompare(String(b.dueDate || '9'))).slice(0, 5);
  return (
    <Shell sources={[{ key: 'invoices', state: st }]} to="/money/invoices" linkLabel="Invoices">
      {open.length === 0 ? <Empty text="No open invoices." /> : (
        <Rows items={open.map((i) => ({ key: i.id, primary: i.party || i.number || 'Invoice', secondary: i.number || undefined, right: <span className="tabular-nums text-foreground">{fmtAmount(i.amount)} · {fmtDay(i.dueDate)}</span> }))} />
      )}
    </Shell>
  );
}

export function ExpensesWidget() {
  const st = useSource('expenses');
  const now = new Date();
  const month = (st.data || []).filter((e) => e.date && new Date(e.date).getMonth() === now.getMonth() && new Date(e.date).getFullYear() === now.getFullYear());
  const cats = Object.entries(month.reduce<Record<string, number>>((m, e) => { const k = e.party || 'Other'; m[k] = (m[k] || 0) + e.amount; return m; }, {}))
    .sort((a, b) => b[1] - a[1]).slice(0, 4);
  return (
    <Shell sources={[{ key: 'expenses', state: st }]} to="/money/expenses" linkLabel="Expenses">
      <div className="space-y-3">
        <Metric value={fmtAmount(month.reduce((n, e) => n + e.amount, 0))} label="Spent this month" />
        {cats.length ? <Rows items={cats.map(([k, v]) => ({ key: k, primary: k, right: <span className="tabular-nums text-foreground">{fmtAmount(v)}</span> }))} /> : <Empty text="No expenses this month." />}
      </div>
    </Shell>
  );
}

// ───────────────────────────── Growth ─────────────────────────────

export function LeadsWidget() {
  const st = useSource('leads');
  const rows = st.data || [];
  const recent = rows.filter((l) => l.createdAt && Date.now() - new Date(l.createdAt).getTime() < 30 * 86400000);
  return (
    <Shell sources={[{ key: 'leads', state: st }]} to="/growth" linkLabel="Growth hub">
      <div className="space-y-3">
        <Metric value={recent.length} label="New leads in the last 30 days" />
        {recent.length > 0 && <Rows items={recent.slice(0, 4).map((l) => ({ key: l.id, primary: l.name, right: l.status ? titleCase(l.status) : undefined }))} />}
      </div>
    </Shell>
  );
}

export function CampaignsWidget() {
  const st = useSource('campaigns');
  const active = (st.data || []).filter((c) => ['active', 'running', 'live'].includes((c.status || '').toLowerCase()));
  return (
    <Shell sources={[{ key: 'campaigns', state: st }]} to="/growth/marketing" linkLabel="Campaigns">
      <div className="space-y-3">
        <Metric value={active.length} label="Active campaigns" />
        {active.length > 0 && <Rows items={active.slice(0, 4).map((c) => ({ key: c.id, primary: c.name }))} />}
      </div>
    </Shell>
  );
}

// ───────────────────────────── Admin / activity ─────────────────────────────

export function UsersWidget() {
  const st = useSource('users');
  const rows = st.data || [];
  const pending = rows.filter((u) => u.status === 'pending');
  return (
    <Shell sources={[{ key: 'users', state: st }]} to="/admin/users?tab=access" linkLabel="User access">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Stat value={rows.filter((u) => u.status === 'active').length} label="Active accounts" tone="success" />
          <Stat value={pending.length} label="Waiting for approval" tone={pending.length ? 'warning' : undefined} />
        </div>
        {pending.length > 0 && <Rows items={pending.slice(0, 3).map((u) => ({ key: u.id, primary: u.name, secondary: u.email }))} />}
      </div>
    </Shell>
  );
}

/** No security event feed exists in the platform yet — said plainly instead of inventing alerts. */
export function SecurityWidget() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
      <Lock className="size-5 text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">Not available yet</p>
      <p className="text-xs text-muted-foreground">The platform does not record security events (such as failed sign-ins) in a feed yet.</p>
    </div>
  );
}

export function RecentActivityWidget() {
  const st = useSource('notifications');
  const rows = (st.data || []).slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 6);
  return (
    <Shell sources={[{ key: 'notifications', state: st }]} to="/notifications" linkLabel="All notifications">
      {rows.length === 0 ? <Empty text="Nothing new." /> : (
        <ul className="space-y-3">
          {rows.map((n) => (
            <li key={n.id} className="flex gap-2.5">
              <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.isRead ? 'bg-border' : 'bg-primary')} aria-label={n.isRead ? 'Read' : 'Unread'} />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{n.title}</p>
                <p className="line-clamp-2 text-xs text-muted-foreground">{n.message}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{ago(n.createdAt)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Shell>
  );
}
